import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useProductRegistry } from "@/data/ProductRegistryContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Database, Tags } from "lucide-react";

function isAutoMappedSku(sku: string): boolean {
  return /-AUTO-/.test(String(sku || "").toUpperCase());
}

export default function AutoMappedSkuReport() {
  const { products, isLoading } = useProductRegistry();

  const autoMapped = useMemo(
    () => products.filter((product) => isAutoMappedSku(product.sku)),
    [products],
  );

  const byCategory = useMemo(() => {
    const map = new Map<string, number>();
    autoMapped.forEach((product) => {
      const key = product.category || "Other";
      map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [autoMapped]);

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Tags className="h-5 w-5" />
            Auto-Mapped SKU Report
          </h1>
          <p className="text-xs text-muted-foreground">
            DB-backed list of products auto-created from Appointment Flow and Build Review mapping.
          </p>
        </div>
        <Button asChild size="sm" variant="outline">
          <Link to="/product-registry">
            <ArrowLeft className="h-3.5 w-3.5 mr-1" />
            Back to Registry
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-muted-foreground">AUTO SKUs</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{autoMapped.length}</p>
          </CardContent>
        </Card>
        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-muted-foreground">BY CATEGORY</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {byCategory.map(([category, count]) => (
              <Badge key={category} variant="secondary">{category}: {count}</Badge>
            ))}
            {byCategory.length === 0 && (
              <span className="text-xs text-muted-foreground">No auto-mapped SKUs found.</span>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Database className="h-4 w-4" />
            Auto-Mapped Items
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-xs text-muted-foreground">Loading latest DB data...</p>
          ) : autoMapped.length === 0 ? (
            <p className="text-xs text-muted-foreground">No auto-mapped SKU entries in product registry.</p>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {autoMapped.map((product) => (
                    <TableRow key={product.productId}>
                      <TableCell className="font-mono text-xs">{product.sku}</TableCell>
                      <TableCell>{product.productName}</TableCell>
                      <TableCell>{product.category}</TableCell>
                      <TableCell>{product.vendor || "—"}</TableCell>
                      <TableCell>{product.active ? "Active" : "Inactive"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

